import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import i18next from 'v2/helpers/i18n';
import {
  REFINE_PROMPT_MIN_LENGTH,
  isRefinePromptReady,
} from 'v2/apps/clink/pages/boq/boqRefinement';
import {
  refineProgressRowSx,
  refineProgressSx,
  refineProgressTextSx,
  refinePromptButtonSx,
  refinePromptCardSx,
  refinePromptContentSx,
  refinePromptHelperSx,
  refinePromptIconSx,
  refinePromptInputSx,
} from './refineStyle';

const RefinePromptCard = ({
  value = '',
  onChange = () => null,
  onSubmit = () => null,
  disabled = false,
  unsavedChanges = false,
  applying = false,
}) => {
  const promptReady = isRefinePromptReady(value);
  const applyDisabled = disabled || applying || !promptReady;
  const showTooShortHelper =
    !applying && !unsavedChanges && Boolean(value.trim()) && !promptReady;
  let helperKey = 'boq-refine-prompt-helper';
  if (unsavedChanges) {
    helperKey = 'boq-refine-prompt-unsaved';
  } else if (showTooShortHelper) {
    helperKey = 'boq-refine-prompt-too-short';
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!applyDisabled) onSubmit(value.trim());
  };

  return (
    <Card
      component="form"
      variant="outlined"
      sx={refinePromptCardSx}
      onSubmit={handleSubmit}
      data-testid="boq-refine-prompt-card"
    >
      <Typography component="label" htmlFor="boq-refine-prompt" fontWeight={600}>
        {i18next.t('boq-refine-prompt-title')}
      </Typography>
      <Box sx={refinePromptContentSx}>
        <AutoAwesomeIcon sx={refinePromptIconSx} aria-hidden />
        <Box>
          <TextField
            id="boq-refine-prompt"
            fullWidth
            size="small"
            value={value}
            disabled={disabled || applying}
            placeholder={i18next.t('boq-refine-prompt-placeholder')}
            onChange={(event) => onChange(event.target.value)}
            slotProps={{
              htmlInput: {
                'aria-label': i18next.t('boq-refine-prompt-title'),
              },
            }}
            sx={refinePromptInputSx}
          />
          {applying ? (
            <Box sx={refineProgressRowSx}>
              <LinearProgress
                aria-label={i18next.t('boq-refine-progress-label')}
                variant="indeterminate"
                sx={refineProgressSx}
              />
              <Typography sx={refineProgressTextSx}>
                {i18next.t('boq-refine-progress-analysing')}
              </Typography>
            </Box>
          ) : (
            <Typography component="div" sx={refinePromptHelperSx}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
              {i18next.t(
                helperKey,
                showTooShortHelper
                  ? { count: REFINE_PROMPT_MIN_LENGTH }
                  : undefined
              )}
            </Typography>
          )}
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={applyDisabled}
          startIcon={
            applying ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <FlashOnIcon />
            )
          }
          sx={refinePromptButtonSx}
        >
          {i18next.t(
            applying ? 'boq-refine-prompt-applying' : 'boq-refine-prompt-apply'
          )}
        </Button>
      </Box>
    </Card>
  );
};

export default RefinePromptCard;
